/**
 * The tools an agency already uses every day, pinned in the hub.
 *
 * This is the cheapest genuinely useful thing the hub does: no integration, no
 * data, just the links people currently keep as browser bookmarks. It is what
 * makes someone open the hub daily rather than when they remember it exists.
 *
 * ⚠️ MEMBER-FACING, SO NOTHING INTERNAL BELONGS HERE (Josh, 2026-08-30).
 * Salesforce is where AAS works, not where members do, and it came off for that
 * reason. The test for anything added later is "would a member agency open
 * this?", not "do we use it?".
 *
 * ⚠️ SOME URLS ARE GENERIC SIGN-IN PAGES. Where AAS has its own tenant address
 * the generic link still works but lands a step earlier than it should. Replace
 * them here and every member gets the better link; there is nothing else to
 * change.
 */
export interface HubApp {
  id: string;
  name: string;
  /** What it is, in the words people use for it — not the vendor's marketing. */
  sub: string;
  url: string;
  /** Two letters for the tile. Kept short so the row stays readable. */
  mark: string;
  color: string;
  /** True when the link is a generic sign-in rather than the AAS tenant. */
  generic?: boolean;
  /**
   * Drawn but not clickable, because we do not have the real URL yet. Better
   * than guessing: a tile that lands somewhere wrong costs more trust than a
   * tile that admits it isn't ready.
   */
  pending?: boolean;
}

export const HUB_APPS: HubApp[] = [
  { id: 'a365', name: 'Alliance365', sub: 'Member portal', url: 'https://www.alliance365.biz', mark: 'A3', color: '#1B4F9C' },
  { id: 'ezlynx', name: 'EZLynx', sub: 'Rating', url: 'https://app.ezlynx.com', mark: 'EZ', color: '#0F9D8C' },
  { id: 'ams360', name: 'AMS360', sub: 'Vertafore', url: 'https://www.ams360.com', mark: 'AM', color: '#2B3A8F' },
  // For agency principals. A Salesforce Experience Cloud site — member-facing,
  // which is what makes it belong here when the Salesforce org itself does not.
  { id: 'agency-portal', name: 'Agency Portal', sub: 'For principals', url: 'https://agentsalliance.my.site.com/Agents/s/', mark: 'AP', color: '#C2410C' },
  { id: 'sharepoint', name: 'SharePoint', sub: 'Shared files', url: 'https://www.office.com/launch/sharepoint', mark: 'SP', color: '#0D7C6B', generic: true },
  { id: 'onedrive', name: 'OneDrive', sub: 'Your files', url: 'https://www.office.com/launch/onedrive', mark: 'OD', color: '#1B6AC9' },
  { id: 'gdrive', name: 'Google Drive', sub: 'Your files', url: 'https://drive.google.com', mark: 'GD', color: '#E3701E' },
];
